import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import UserStatusIcon from "../../components/UserModal/statusIcon";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";



const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: '12px',
    boxShadow: theme.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  departmentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    justifyContent: 'center',
    maxWidth: '200px',
    alignItems: 'flex-start',
    margin: '0 auto',
    width: '100%',
    maxHeight: '60px',
    overflow: 'hidden',
    position: 'relative',
  },
  departmentChip: {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '3px 6px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: 'bold',
    maxWidth: '100%',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: '1.2',
    textAlign: 'center',
    display: 'inline-block',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    margin: '1px',
  },
  departmentOverflow: {
    position: 'absolute',
    bottom: '0',
    right: '0',
    backgroundColor: theme.mode === 'dark' ? '#6b7280' : '#666',
    color: 'white',
    padding: '2px 4px',
    borderRadius: '8px',
    fontSize: '9px',
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    minWidth: '36px',
    height: '36px',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    },
  },
  editButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  deleteButton: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  addButton: {
    borderRadius: '8px',
    padding: '8px 16px',
    fontWeight: 'bold',
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
    },
  },
  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
    fontWeight: 'bold',
    fontSize: '14px',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    },
    transition: 'background-color 0.2s ease',
    '& td': {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : 'rgba(224, 224, 224, 0.5)'}`,
    },
  },
  profileBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  adminBadge: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  userBadge: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
}));

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-user`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-user`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
    };
  }, [socketManager]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      <MainHeader>
        <Title>{i18n.t("users.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Can
            role={user.profile}
            perform="user-modal:editProfile"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
                className={classes.addButton}
                startIcon={<EditIcon />}
              >
                {i18n.t("users.buttons.add")}
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
			<TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.id")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>{i18n.t("users.table.status")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>{i18n.t("users.table.name")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.email")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.profile")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader} style={{ width: '200px', minWidth: '200px' }}>
                {i18n.t("users.table.department")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {users.map((user) => (
                <TableRow key={`user-row-${user.id}`} className={classes.tableRow}>
				  <TableCell align="center">{user.id}</TableCell>
				  <TableCell align="center"><UserStatusIcon user={user} /></TableCell>
                  <TableCell align="center" style={{ fontWeight: '500' }}>{user.name}</TableCell>
                  <TableCell align="center">{user.email}</TableCell>
                  <TableCell align="center">
                    <span className={`${classes.profileBadge} ${
                      user.profile === 'admin' ? classes.adminBadge : classes.userBadge
                    }`}>
                      {user.profile}
                    </span>
                  </TableCell>
                  <TableCell align="center" style={{ width: '200px', minWidth: '200px' }}>
                    {user.queues && user.queues.length > 0 ? (
                      <div className={classes.departmentContainer}>
                        {user.queues.slice(0, 3).map((queue, index) => (
                          <span
                            key={`user-${user.id}-queue-${queue.id || 'unknown'}-${index}`}
                            className={classes.departmentChip}
                            style={{
                              backgroundColor: queue.color || '#1976d2',
                            }}
                          >
                            {queue.name}
                          </span>
                        ))}
                        {user.queues.length > 3 && (
                          <span className={classes.departmentOverflow}>
                            +{user.queues.length - 3} más
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px' }}>Sin departamento</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.actionButtons}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        className={`${classes.actionButton} ${classes.editButton}`}
                        title="Editar usuario"
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          if (user.id === 1) {
                            toast.error("❌ No se puede eliminar el Super Usuario Master (ID: 1). Esta es la cuenta de configuración por defecto del sistema.");
                            return;
                          }
                          setConfirmModalOpen(true);
                          setDeletingUser(user);
                        }}
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        title={user.id === 1 ? "No se puede eliminar el Super Usuario Master" : "Eliminar usuario"}
                        disabled={user.id === 1}
                        style={{
                          opacity: user.id === 1 ? 0.5 : 1,
                          cursor: user.id === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton key="loading-skeleton" columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Users;
